import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { Group, Prompt } from "@1js/pl-types";
import {
  Button,
  Checkbox,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  Link,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
  useId,
  type CheckboxOnChangeData,
  type ComboboxOpenChangeData,
  type ComboboxOpenEvents,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import * as React from "react";
import { GroupOption } from "../GroupOption/GroupOption";
import { PromptCard } from "../PromptCard/PromptCard";
import type { Appearance } from "../data";
import {
  CloseTooltip,
  LearnHowToCreateATeamLink,
  NotifyTeamMemberCheckboxSpinnerAriaLabel,
  NotifyTeamMembersCheckboxLabel,
  NotifyTeamMembersWithCountCheckboxLabel,
  ShareToTeamCancelButtonLabel,
  ShareToTeamExplanation,
  ShareToTeamShareButtonLabel,
  ShareToTeamTeamsDropdownPlaceholder,
  ShareToTeamTeamsLabel,
  ShareToTeamTitle,
} from "./ShareToWorkgroupDialog.strings";

export type ShareToWorkgroupDialogProps = {
  groups?: Group[];
  prompts?: Prompt[];
  isOpen: boolean;
  onClose: (isClosedByCustomer: boolean, prompts: Prompt[]) => void;
  onShare: (prompts: Prompt[], groupIds: string[], isNotify?: boolean) => void;
  appearance: Appearance;
  isAllowMultiSelect: boolean;
  isNotificationEnabled?: boolean;
  onGroupDropdownOpenChange?: (prompt: Prompt, isOpen: boolean) => void;
  onGroupDropdownOptionSelect?: (
    prompt: Prompt,
    selectedGroups?: string[]
  ) => void;
  notificationMemberCounts?: Map<string, number>;
  isFetchingGroupMembers?: boolean;
};

const useStyles = makeStyles({
  explanation: {
    marginBottom: "14px",
  },
  promptCardContainer: {
    backgroundImage: [
      "linear-gradient(90deg, rgba(71, 207, 250, 0.10) 0%, rgba(180, 124, 248, 0.10) 100%)",
    ],
    paddingTop: "20px",
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    marginBottom: "14px",
  },
  promptCardContainerInner: {
    maxWidth: "260px",
  },
  combobox: {
    width: "100%",
  },
  spacerBottom: {
    marginBottom: "36px",
  },
  listbox: {
    maxHeight: "185px",
  },
  notificationCheckbox: {},
  notificationContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "8px",
    marginBottom: "24px",
  },
  createATeamLink: {
    color: tokens.colorBrandForeground1,
  },
});

export const ShareToWorkgroupDialog: React.FC<ShareToWorkgroupDialogProps> = ({
  appearance,
  groups,
  isAllowMultiSelect,
  isNotificationEnabled = false,
  isOpen,
  notificationMemberCounts,
  onClose,
  onGroupDropdownOpenChange,
  onGroupDropdownOptionSelect,
  onShare,
  prompts,
  isFetchingGroupMembers,
}) => {
  const [groupIds, setGroupIds] = React.useState<string[]>([]);
  const [isButtonEnabled, setIsButtonEnabled] = React.useState(false);
  const [isNotificationRequested, setIsNotificationRequested] =
    React.useState(false);
  const styles = useStyles();

  const handleClose = React.useCallback(() => {
    onClose(true, prompts as Prompt[]);
  }, [onClose, prompts]);

  const handleShare = React.useCallback(() => {
    onShare(prompts as Prompt[], groupIds, isNotificationRequested);
    onClose(false, prompts as Prompt[]);
  }, [groupIds, isNotificationRequested, onClose, onShare, prompts]);

  const localizations = useStrings({
    ShareToTeamCancelButtonLabel,
    ShareToTeamShareButtonLabel,
    ShareToTeamTitle,
    ShareToTeamExplanation,
    ShareToTeamTeamsLabel,
    ShareToTeamTeamsDropdownPlaceholder,
    CloseTooltip,
    LearnHowToCreateATeamLink,
    NotifyTeamMembersCheckboxLabel,
    NotifyTeamMemberCheckboxSpinnerAriaLabel,
  });

  const localizationsWithPlaceholder = useStringsWithPlaceholders({
    NotifyTeamMembersWithCountCheckboxLabel,
  });

  React.useEffect(() => {
    if (!isOpen) {
      // reset the dialog state when it's closed
      setGroupIds([]);
      setIsButtonEnabled(false);
      setIsNotificationRequested(false);
    }
  }, [isOpen]);

  const onSelectedGroupChange = React.useCallback(
    (_event: SelectionEvents, data: OptionOnSelectData) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      setGroupIds(data.selectedOptions);
      setIsButtonEnabled(data.selectedOptions.length > 0);
      const prompt = prompts?.[0];
      if (prompt && onGroupDropdownOptionSelect) {
        onGroupDropdownOptionSelect(prompt, data.selectedOptions);
      }
    },
    [onGroupDropdownOptionSelect, prompts]
  );

  const handleOnGroupDropdownOpenChange = React.useCallback(
    (_: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
      const prompt = prompts?.[0];
      if (prompt && onGroupDropdownOpenChange) {
        onGroupDropdownOpenChange(prompt, data.open);
      }
    },
    [prompts, onGroupDropdownOpenChange]
  );

  const handleNotificationRequestedChange = React.useCallback(
    (
      _event: React.ChangeEvent<HTMLInputElement>,
      data: CheckboxOnChangeData
    ) => {
      setIsNotificationRequested(data.checked as boolean);
    },
    []
  );

  let notificationMemberCount = 0;
  if (isNotificationEnabled && notificationMemberCounts) {
    notificationMemberCount = groupIds.reduce((acc, groupId) => {
      const count = notificationMemberCounts.get(groupId);
      return acc + (count ?? 0);
    }, 0);
  }

  const groupDropdownId = useId("group-dropdown");

  if (!prompts || prompts.length === 0 || !groups || groups.length === 0) {
    return <></>;
  }

  const promptToShare = (prompts as Prompt[])[0];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Tooltip
                content={localizations.CloseTooltip()}
                relationship="label"
              >
                <DialogTrigger action="close">
                  <Button
                    data-testid="close-dialog"
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss20Regular />}
                  />
                </DialogTrigger>
              </Tooltip>
            }
          >
            {localizations.ShareToTeamTitle()}
          </DialogTitle>
          <DialogContent>
            <div className={styles.explanation}>
              {localizations.ShareToTeamExplanation()}
            </div>
            <div className={styles.promptCardContainer}>
              <div className={styles.promptCardContainerInner}>
                <PromptCard
                  prompt={promptToShare}
                  appearance={appearance}
                  isPromptSharingEnabled={false}
                  isPromptSharingViaEmailEnabled={false}
                  isPromptSharingToWorkgroupEnabled={false}
                  isPromptRemovingFromWorkgroupEnabled={false}
                  isPromptRingTagEnabled={false}
                  isBadgesEnabled={false}
                  isFavoriteEnabled={false}
                  isShowPromptAuthor={true}
                />
              </div>
            </div>
            <Label id={groupDropdownId} required={true} size="large">
              {localizations.ShareToTeamTeamsLabel()}
            </Label>
            <br />
            <Combobox
              required={true}
              placeholder={localizations.ShareToTeamTeamsDropdownPlaceholder()}
              selectedOptions={groupIds}
              onOptionSelect={onSelectedGroupChange}
              aria-labelledby={groupDropdownId}
              size="large"
              className={mergeClasses(
                styles.combobox,
                !isNotificationEnabled ? styles.spacerBottom : ""
              )}
              data-testid="group-combobox"
              multiselect={isAllowMultiSelect}
              onOpenChange={handleOnGroupDropdownOpenChange}
              inlinePopup={true}
              listbox={{ className: styles.listbox }}
            >
              {groups
                ?.filter((group: Group) => group.IsMember)
                .map((group: Group) => (
                  <GroupOption group={group} key={group.Id} />
                ))}
            </Combobox>
            {isNotificationEnabled && (
              <div className={styles.notificationContainer}>
                <Checkbox
                  className={styles.notificationCheckbox}
                  size="medium"
                  label={
                    !isFetchingGroupMembers &&
                    notificationMemberCounts &&
                    groupIds.length !== 0
                      ? localizationsWithPlaceholder.NotifyTeamMembersWithCountCheckboxLabel(
                          { count: notificationMemberCount.toString() }
                        )
                      : localizations.NotifyTeamMembersCheckboxLabel()
                  }
                  checked={isNotificationRequested}
                  onChange={handleNotificationRequestedChange}
                />
                {isFetchingGroupMembers && (
                  <Spinner
                    size="small"
                    aria-label={localizations.NotifyTeamMemberCheckboxSpinnerAriaLabel()}
                  />
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions position="start">
            <Link
              as="a"
              href="https://support.microsoft.com/office/create-a-team-from-scratch-in-microsoft-teams-174adf5f-846b-4780-b765-de1a0a737e2b"
              target="_blank"
              className={styles.createATeamLink}
              inline
            >
              {localizations.LearnHowToCreateATeamLink()}
            </Link>
          </DialogActions>
          <DialogActions position="end">
            <Button
              appearance="primary"
              onClick={handleShare}
              disabled={!isButtonEnabled}
            >
              {localizations.ShareToTeamShareButtonLabel()}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {localizations.ShareToTeamCancelButtonLabel()}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
